/**
 * Nafath — MOIA backend + ELM test MFA update.
 *
 * Flow: initiate → poll status (WAITING) → [ELM test: status COMPLETED] → poll until COMPLETED + dataReady + identity.
 */

import axios from "axios";
import { API_BASE_URL } from "../config";

/**
 * By default we point Nafath calls to MOIA host (matches Postman testing).
 * - Override via REACT_APP_NAFATH_BASE_URL if you need a different backend.
 * - We avoid relying on CRA proxy defaults which may target localhost backends.
 */
const DEFAULT_NAFATH_BASE_URL = "https://emasajid.moia.gov.sa";

const getNafathBaseUrl = () => {
  const override = (process.env.REACT_APP_NAFATH_BASE_URL || "").trim();
  if (override) return override.replace(/\/+$/, "");

  // Local development: default to MOIA host (matches your Postman testing).
  // If you want to use a local/swagger backend, set REACT_APP_NAFATH_BASE_URL explicitly.
  if (process.env.NODE_ENV === "development") return DEFAULT_NAFATH_BASE_URL;

  // In production builds, prefer your configured API_BASE_URL if present; otherwise fall back to MOIA.
  const configured = (API_BASE_URL || "").toString().trim();
  if (configured) return configured.replace(/\/+$/, "");

  return DEFAULT_NAFATH_BASE_URL;
};

/** ELM staging — may be CORS-blocked from browser; use backend proxy if needed. */
export const NAFATH_ELM_TEST_MFA_UPDATE_URL =
  "https://rabet-nafath.api.elm.sa/nafath-test-app/api/v1/mfa/request/update";

export type InitiateNafathInput = {
  nationalId: string;
};

export type InitiateNafathResponse = {
  success?: boolean;
  requestId?: string;
  transId?: string;
  random?: string;
};

export type NafathIdentity = {
  nationalId?: string;
  fullNameEn?: string;
  fullNameAr?: string;
  dobGregorian?: string;
  dobHijri?: string;
  gender?: string;
  nationality?: string;
  callbackReceivedAt?: string;
  completedAt?: string;
};

export type NafathStatusResponse = {
  success?: boolean;
  /** e.g. WAITING | COMPLETED */
  status?: string;
  dataReady?: boolean;
  source?: string;
  requestId?: string;
  transId?: string;
  identity?: NafathIdentity;
};

export type NafathStatusInput = {
  transId: string;
  /** Some backend implementations require these for status lookup */
  nationalId?: string;
  random?: string;
};

export type UpdateNafathMfaTestInput = {
  transId: string;
  /** Defaults to COMPLETED for ELM test simulation */
  status?: string;
};

function toAxiosMessage(err: unknown, fallback: string): string {
  const anyErr = err as { response?: { data?: unknown; status?: number }; message?: string };
  const body = anyErr?.response?.data as Record<string, unknown> | string | undefined;
  const status = anyErr?.response?.status;
  if (typeof body === "string" && body.trim()) return body.trim();
  if (body && typeof body === "object") {
    const msg =
      (body.message as string) ||
      (body.detail as string) ||
      (body.title as string) ||
      (body.error as string);
    if (msg && String(msg).trim()) return String(msg).trim();
  }
  return anyErr?.message || (status === 400 ? fallback : "حدث خطأ أثناء الاتصال.");
}

/** Path segment matches Swagger: `/api/nafath/...` (lowercase) */
const NAFATH_API_PREFIX = "/api/nafath";

/**
 * POST /api/nafath/initiate-nafath
 */
export async function initiateNafath(
  input: InitiateNafathInput
): Promise<InitiateNafathResponse> {
  const url = `${getNafathBaseUrl()}${NAFATH_API_PREFIX}/initiate-nafath`;
  try {
    const { data } = await axios.post<InitiateNafathResponse>(
      url,
      { nationalId: input.nationalId.replace(/\D/g, "") },
      {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        validateStatus: (s) => s >= 200 && s < 300,
        timeout: 60000,
      }
    );
    return data ?? {};
  } catch (err: unknown) {
    const message = toAxiosMessage(err, "فشل بدء طلب نفاذ.");
    const e = new Error(message) as Error & { status?: number; body?: unknown };
    e.status = (err as { response?: { status?: number } })?.response?.status;
    e.body = (err as { response?: { data?: unknown } })?.response?.data;
    throw e;
  }
}

/**
 * POST /api/nafath/status — body { transId }
 */
export async function getNafathStatus(
  input: NafathStatusInput
): Promise<NafathStatusResponse> {
  const url = `${getNafathBaseUrl()}${NAFATH_API_PREFIX}/status`;
  try {
    const { data } = await axios.post<NafathStatusResponse>(
      url,
      {
        transId: input.transId,
        ...(input.nationalId ? { nationalId: input.nationalId.replace(/\D/g, "") } : null),
        ...(input.random != null && String(input.random).trim() !== ""
          ? { random: String(input.random).trim() }
          : null),
      },
      {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        validateStatus: (s) => s >= 200 && s < 300,
        timeout: 60000,
      }
    );
    return (data ?? {}) as NafathStatusResponse;
  } catch (err: unknown) {
    const message = toAxiosMessage(err, "فشل جلب حالة نفاذ.");
    const e = new Error(message) as Error & { status?: number; body?: unknown };
    e.status = (err as { response?: { status?: number } })?.response?.status;
    e.body = (err as { response?: { data?: unknown } })?.response?.data;
    throw e;
  }
}

/**
 * ELM test app — simulate completion for staging (same as Postman).
 * Browser CORS may fail; then proxy via your API.
 */
export async function updateNafathMfaRequestTest(
  input: UpdateNafathMfaTestInput
): Promise<unknown> {
  const body = {
    transId: input.transId,
    status: input.status ?? "COMPLETED",
  };
  try {
    const { data } = await axios.post(NAFATH_ELM_TEST_MFA_UPDATE_URL, body, {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      validateStatus: (s) => s >= 200 && s < 300,
      timeout: 60000,
    });
    return data;
  } catch (err: unknown) {
    const message = toAxiosMessage(err, "فشل تحديث طلب نفاذ (ELM).");
    const e = new Error(message) as Error & { status?: number; body?: unknown };
    e.status = (err as { response?: { status?: number } })?.response?.status;
    e.body = (err as { response?: { data?: unknown } })?.response?.data;
    throw e;
  }
}

/** True when MOIA reports completed identity payload is ready */
export function isNafathCompleted(r: NafathStatusResponse): boolean {
  const st = (r.status || "").toUpperCase();
  return st === "COMPLETED" && r.dataReady === true && !!r.identity;
}
